import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api, { academicService, parentService, userService, enrollmentService, fetchAllPages } from '../services/api';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import DashboardHero from '../components/DashboardHero';
import UpcomingEvents from '../components/UpcomingEvents';
import QuickActions from '../components/QuickActions';
import { StatCard, RadialProgress, HorizontalBarChart, TrendChart } from '../components/ui';
import { bucketByWeek } from '../utils/chartHelpers';
import { useTranslation } from 'react-i18next';

function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [courseProgress, setCourseProgress] = useState([]);
  const [scoreTrend, setScoreTrend] = useState([]);
  const [attemptsTrend, setAttemptsTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [linkRequests, setLinkRequests] = useState([]);
  const { t } = useTranslation();

  const isStudent = user?.roles?.some(r => r.name.toLowerCase() === 'student');
  const isTeacher = user?.roles?.some(r => r.name.toLowerCase() === 'teacher');

  const fetchLinkRequests = async () => {
    try {
      const res = await parentService.getLinks();
      const data = res.data;
      const list = Array.isArray(data) ? data : data.results || [];
      setLinkRequests(list.filter((l) => l.status === 'pending' && l.student?.id === user.id));
    } catch (err) {
      console.error('Error fetching parent link requests:', err);
    }
  };

  const handleDecideLink = async (id, decision) => {
    try {
      if (decision === 'approve') {
        await parentService.approveLink(id);
      } else {
        await parentService.rejectLink(id);
      }
      setLinkRequests((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      console.error('Error deciding parent link request:', err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await academicService.getMyModules();
        const data = response.data;
        setModules(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        console.error('Error fetching my modules:', err);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    // Real per-role numbers from the backend — the module list alone can't
    // tell us completed lessons, quiz attempts, or student counts.
    userService.getDashboard()
      .then((res) => setStats(res.data.stats || {}))
      .catch(() => setStats({}));

    enrollmentService.getEvents()
      .then((res) => setEvents(res.data.results || res.data || []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));

    if (isStudent) {
      // Aggregated client-side into per-course completion — the endpoint
      // itself already scopes to "my own progress" for a student caller.
      api.get('/content/lesson-progress/')
        .then((res) => {
          const rows = res.data.results || res.data || [];
          const byModule = {};
          rows.forEach((r) => {
            const key = r.module_name || t('Unknown');
            if (!byModule[key]) byModule[key] = { total: 0, completed: 0 };
            byModule[key].total += 1;
            if (r.is_completed) byModule[key].completed += 1;
          });
          setCourseProgress(Object.entries(byModule).map(([label, v]) => ({
            label,
            value: v.total ? Math.round((v.completed / v.total) * 100) : 0,
          })));
        })
        .catch(() => setCourseProgress([]));

      // Chronological score trend — the endpoint scopes to the caller's
      // own attempts, so no student filter is needed here.
      fetchAllPages('/quizzes/attempts/')
        .then((rows) => {
          const sorted = [...rows].sort((a, b) => new Date(a.started_at) - new Date(b.started_at)).slice(-10);
          setScoreTrend(sorted.map((a) => ({
            label: new Date(a.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
            value: Math.round(Number(a.score)),
          })));
        })
        .catch(() => setScoreTrend([]));
    }

    if (isTeacher) {
      // Attempts on quizzes belonging to this teacher's modules, bucketed
      // into weekly volume — same endpoint, scoped server-side by role.
      fetchAllPages('/quizzes/attempts/')
        .then((rows) => setAttemptsTrend(bucketByWeek(rows, 'started_at')))
        .catch(() => setAttemptsTrend([]));
    }

    fetchModules();
    if (isStudent) fetchLinkRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-primary-600 text-lg">Loading...</div>
      </div>
    );
  }

  const s = stats || {};
  const statTiles = isTeacher
    ? [
        { label: t('My Modules'), value: s.my_modules ?? '—', color: 'blue', onClick: () => navigate('/courses') },
        { label: t('My Students'), value: s.my_students ?? '—', color: 'green', onClick: () => navigate('/students') },
        { label: t('My Lessons'), value: s.my_lessons ?? '—', color: 'yellow' },
      ]
    : [
        { label: t('Active Courses'), value: s.active_enrollments ?? '—', color: 'blue', onClick: () => navigate('/courses') },
        { label: t('Completed Lessons'), value: s.completed_lessons ?? '—', color: 'green' },
        { label: t('Quiz Attempts'), value: s.quiz_attempts ?? '—', color: 'yellow' },
      ];

  const heroStat = isTeacher
    ? { label: t('My Students'), value: s.my_students ?? '—' }
    : { label: t('Avg. Quiz Score'), value: s.average_quiz_score != null ? `${s.average_quiz_score}%` : '—' };

  // Both already ride along on the modules fetch every teacher dashboard
  // already makes — no extra request for this chart.
  const studentsByModule = modules.map((m) => ({ label: m.name, value: m.students_count ?? 0 }));
  const lessonsByModule = modules.map((m) => ({ label: m.name, value: m.lessons_count ?? 0 }));

  const quickActions = [
    {
      label: isTeacher ? t('Create Module') : t('Browse Courses'),
      color: 'primary',
      onClick: () => (isTeacher ? navigate('/modules/create') : navigate('/courses')),
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
      ),
    },
    {
      label: t('View Calendar'),
      color: 'green',
      onClick: () => navigate('/calendar'),
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      ),
    },
    {
      label: t('Open Messages'),
      color: 'purple',
      onClick: () => navigate('/messages'),
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
      ),
    },
    {
      label: isTeacher ? t('My Students') : t('My Enrollments'),
      color: 'orange',
      onClick: () => navigate(isTeacher ? '/students' : '/enrollments'),
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
      ),
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Dashboard')} />
        <main className="flex-1 overflow-y-auto p-6">
          <DashboardHero
            greeting={t('Welcome back', { name: user.first_name })}
            subtitle={isTeacher
              ? t('{{count}} modules in your hands', { count: s.my_modules ?? 0 })
              : t('{{count}} courses in progress', { count: s.active_enrollments ?? 0 })}
            stat={heroStat}
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {statTiles.map((tile) => (
              <StatCard key={tile.label} {...tile} />
            ))}
            <div className="relative overflow-hidden rounded-2xl bg-purple-50 p-4 text-left">
              <p className="text-xs font-medium text-purple-700 opacity-80 truncate">{isTeacher ? t('My Quizzes') : t('Avg. Quiz Score')}</p>
              {isTeacher ? (
                <p className="text-3xl font-display font-bold text-purple-700 mt-1 tabular-nums">{s.my_quizzes ?? '—'}</p>
              ) : (
                <div className="mt-1">
                  <RadialProgress value={s.average_quiz_score ?? 0} size={44} strokeWidth={5} color="#9333ea" trackColor="#f3e8ff" />
                </div>
              )}
            </div>
          </div>

          {isStudent && linkRequests.length > 0 && (
            <div className="bg-white rounded-2xl shadow-card p-4 mb-4 border-l-4 border-yellow-500">
              <h3 className="text-sm font-bold text-gray-800 mb-3">{t('Parent Link Requests')}</h3>
              <div className="space-y-2">
                {linkRequests.map((link) => (
                  <div key={link.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{link.parent?.first_name} {link.parent?.last_name}</p>
                      <p className="text-xs text-gray-500">{link.parent?.email} {t('wants to link to your account as')} {link.relationship || t('parent')}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleDecideLink(link.id, 'approve')} className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-xs font-medium">
                        {t('Approve')}
                      </button>
                      <button onClick={() => handleDecideLink(link.id, 'reject')} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-xs font-medium">
                        {t('Reject')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-card p-4">
              <h3 className="text-sm font-display font-bold text-gray-800 mb-3">
                {isTeacher ? t('Recent Modules', { what: t('My Modules') }) : t('Recent Modules', { what: t('My Courses') })}
              </h3>
              {loading ? (
                <div className="text-center py-8 text-gray-500 text-sm">{t('Loading...') || 'Loading...'}</div>
              ) : modules.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">{t('No items found', { what: isTeacher ? t('My Modules') : t('My Courses') })}</div>
              ) : (
                <div className="space-y-2">
                  {modules.slice(0, 4).map((module) => (
                    <div key={module.id} onClick={() => navigate('/courses')} className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition cursor-pointer">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {module.code?.substring(0, 2)}
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <h4 className="font-medium text-gray-800 text-sm truncate">{module.name}</h4>
                        <p className="text-xs text-gray-500">{module.code} • {module.grade?.name || 'N/A'}</p>
                      </div>
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <QuickActions actions={quickActions} />
              <UpcomingEvents events={events} loading={eventsLoading} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {isTeacher ? (
              <>
                <div className="bg-white rounded-2xl shadow-card p-4">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Students by Module')}</h3>
                  <HorizontalBarChart bars={studentsByModule} valueSuffix="" emptyLabel={t('No items found', { what: t('My Modules') })} />
                </div>
                <div className="bg-white rounded-2xl shadow-card p-4">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Lessons by Module')}</h3>
                  <HorizontalBarChart bars={lessonsByModule} valueSuffix="" emptyLabel={t('No items found', { what: t('My Modules') })} />
                </div>
                <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Quiz Attempts Over Time')}</h3>
                  <TrendChart points={attemptsTrend} color="#f7930d" valueSuffix="" emptyLabel={t('No items found', { what: t('Quiz Attempts Over Time') })} />
                </div>
              </>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Progress by Course')}</h3>
                  <HorizontalBarChart bars={courseProgress} emptyLabel={t('No items found', { what: t('My Courses') })} />
                </div>
                <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
                  <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Quiz Score Trend')}</h3>
                  <TrendChart points={scoreTrend} color="#f7930d" valueSuffix="%" emptyLabel={t('No items found', { what: t('Quiz Score Trend') })} />
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
